import React, { useState } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const UploadCV = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [aiExtraction, setAiExtraction] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please upload a file smaller than 5MB',
          variant: 'destructive'
        });
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = () => {
    if (!file) return;
    
    setUploading(true);
    
    // Mock upload with AI extraction
    setTimeout(() => {
      setUploading(false);
      setUploaded(true);
      
      // Mock AI extraction results
      setAiExtraction({
        name: 'Dr. Sarah Mitchell',
        profession: 'Registered Nurse',
        specialisation: 'Critical Care',
        experience: 8,
        skills: ['ICU', 'Emergency Care', 'Patient Assessment', 'Ventilator Management', 'ACLS', 'BLS'],
        education: [
          { degree: 'Bachelor of Science in Nursing', institution: 'University of Manchester', year: 2015 },
          { degree: 'Critical Care Certification', institution: 'Royal College of Nursing', year: 2017 }
        ],
        licenses: [
          { type: 'UK Nursing License', number: 'NMC12345', expiry: '2026-12-31' }
        ]
      });
      
      toast({
        title: 'CV Uploaded Successfully',
        description: 'Our AI has extracted your information. Please review it below.',
      });
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Upload Your CV</h1>
            <p className="text-gray-600">Our AI will automatically extract your skills, experience, and qualifications</p>
          </div>

          {!uploaded ? (
            <Card>
              <CardHeader>
                <CardTitle>Upload CV Document</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-[#42b1c2] transition-colors">
                  <Upload className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Drop your CV here or click to browse</h3>
                  <p className="text-sm text-gray-600 mb-4">Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
                  
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    id="cv-upload"
                  />
                  <label htmlFor="cv-upload">
                    <Button type="button" className="bg-[#42b1c2] hover:bg-[#3a9fb0] text-white" asChild>
                      <span>Select File</span>
                    </Button>
                  </label>
                  
                  {file && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-center space-x-3">
                        <FileText className="h-6 w-6 text-[#42b1c2]" />
                        <div>
                          <p className="font-medium text-gray-900">{file.name}</p>
                          <p className="text-sm text-gray-600">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <Button 
                        onClick={handleUpload} 
                        className="mt-4 bg-[#42b1c2] hover:bg-[#3a9fb0] text-white"
                        disabled={uploading}
                      >
                        {uploading ? 'Uploading & Processing...' : 'Upload & Process with AI'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              <Card className="border-2 border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">CV Uploaded Successfully</h3>
                      <p className="text-sm text-gray-600">AI extraction completed. Please review the information below.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* AI Extracted Information */}
              <Card>
                <CardHeader>
                  <CardTitle>AI Extracted Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Personal Information</h3>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Name:</span>
                        <span className="ml-2 font-medium">{aiExtraction?.name}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Profession:</span>
                        <span className="ml-2 font-medium">{aiExtraction?.profession}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Specialisation:</span>
                        <span className="ml-2 font-medium">{aiExtraction?.specialisation}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Experience:</span>
                        <span className="ml-2 font-medium">{aiExtraction?.experience} years</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Extracted Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {aiExtraction?.skills.map((skill, index) => (
                        <span key={index} className="px-3 py-1 bg-[#42b1c2]/10 text-[#42b1c2] rounded-full text-sm font-medium">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Education</h3>
                    <div className="space-y-3">
                      {aiExtraction?.education.map((edu, index) => (
                        <div key={index} className="border-l-2 border-[#42b1c2] pl-4">
                          <p className="font-medium text-gray-900">{edu.degree}</p>
                          <p className="text-sm text-gray-600">{edu.institution}</p>
                          <p className="text-sm text-gray-500">{edu.year}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Licenses</h3>
                    <div className="space-y-2">
                      {aiExtraction?.licenses.map((license, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-900">{license.type}</p>
                            <p className="text-sm text-gray-600">License #{license.number}</p>
                            <p className="text-sm text-gray-500">Valid until {license.expiry}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t flex space-x-4">
                    <Button className="bg-[#42b1c2] hover:bg-[#3a9fb0] text-white">
                      Confirm & Save
                    </Button>
                    <Button variant="outline" onClick={() => { setUploaded(false); setFile(null); }}>
                      Upload Different CV
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tips */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="text-lg">Tips for Best Results</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                  <span>Use a clear, well-formatted CV with proper headings</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                  <span>Include all relevant certifications and licenses</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                  <span>List specific clinical skills and specialisations</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                  <span>Include dates for experience and education</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default UploadCV;